import { api } from "@/config/api";

export interface UsageTotals {
  requests: number;
  tokensIn: number;
  tokensOut: number;
  cachedInputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  reasoningTokens: number;
  costUsd: number;
  platformCostUsd: number;
  credits: number;
  customerChargeUsd: number | null;
  grossMarginUsd: number | null;
  financialRequests: number;
  unpricedRequests: number;
  failures: number;
  pending: number;
  succeeded: number;
  averageLatencyMs: number | null;
  retries: number;
  imagesGenerated: number;
  videoSecondsGenerated: number;
  audioInputSeconds: number;
  audioOutputSeconds: number;
  characters: number;
  reservedCredits: number;
}
export interface UsageGroup extends UsageTotals {
  _id: string;
  companyUid?: string;
  companyName?: string;
}
export interface UsageReport {
  days: number;
  from: string;
  asOf: string;
  totals: UsageTotals;
  byDay: UsageGroup[];
  byProvider: UsageGroup[];
  byFeature: UsageGroup[];
  byModel: UsageGroup[];
  byCompany: UsageGroup[];
  byBillingMode: UsageGroup[];
}
export interface PageResult<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}
export interface RequestRow {
  _id: string;
  companyUid: string;
  companyName: string;
  provider: string;
  model: string;
  feature: string;
  tokensIn: number;
  tokensOut: number;
  costUsd?: number | null;
  creditsCharged: number;
  keyScope: string;
  createdAt: string;
  usageUid?: string;
  userUid?: string;
  status: "pending" | "succeeded" | "failed";
  billingMode: string;
  requestType?: string;
  costStatus: string;
  platformCostUsd?: number | null;
  customerChargeUsd?: number;
  grossMarginUsd?: number;
  cachedInputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  reasoningTokens?: number;
  latencyMs?: number;
  retryCount?: number;
  errorCategory?: string;
  imagesGenerated?: number;
  videoSecondsGenerated?: number;
  audioInputSeconds?: number;
  audioOutputSeconds?: number;
  characters?: number;
  priceUid?: string;
  reservedIncludedCredits?: number;
  reservedTopupCredits?: number;
  reservedCostUsd?: number;
  unfundedCredits?: number;
}
export interface AiWalletBalance {
  period: string;
  includedMonthlyCredits: number;
  includedUsed: number;
  used: number;
  reserved: number;
  includedRemaining: number;
  grantedCredits: number;
  grantedAvailable: number;
  debtCredits: number;
  balance: number;
  usedPercent: number;
  warningLevel: "normal" | "warning" | "critical" | "exhausted";
  byFeature: Array<{ _id: string; credits: number }>;
  byDay: Array<{ _id: string; credits: number }>;
}
export interface AiCreditEntry {
  entryUid: string;
  companyUid: string;
  kind: string;
  credits: number;
  reason: string;
  actorUid: string;
  usageUid?: string;
  createdAt: string;
}
export interface WalletRow {
  companyUid: string;
  name: string;
  status: string;
  wallet:
    | (AiWalletBalance & {
        aiDailyCostCapUsd?: number;
        aiMonthlyCostCapUsd?: number;
      })
    | null;
  walletError?: string;
}

export interface ModelCatalog {
  creditUsdRate: number;
  providers: Array<{
    id: string;
    label: string;
    defaultModel: string;
    consoleUrl: string;
    models: Array<{
      id: string;
      label: string;
      contextWindow?: number;
      tier?: string;
      pricing: { in: number; out: number } | null;
    }>;
  }>;
}
export interface WhatsappHealthRow {
  companyUid: string;
  companyName: string;
  accountUid: string;
  businessName: string;
  verifiedName?: string;
  displayPhoneNumber: string;
  wabaId?: string;
  isActive: boolean;
  isDefault?: boolean;
  qualityRating?: string;
  messagingLimitTier?: string;
  botMode?: string;
  healthFetchedAt?: string;
  healthAttemptedAt?: string;
  healthError?: string;
  registrationFailed?: boolean;
  webhookFailed?: boolean;
  webhookSubscriptionStatus?: string;
  webhookSubscribedAt?: string;
  webhookCheckedAt?: string;
  webhookSubscriptionError?: string;
  lastWebhookAt?: string;
  lastInboundAt?: string;
  cooldownUntil?: string;
  reconnectWarning?: string | null;
}
export interface WhatsappHealthSummary { total: number; active: number; lowQuality: number; webhookProblems: number; healthErrors: number }
export interface WebhookHealth {
  failed: number;
  processing: number;
  completed: number;
}
export interface PlatformOverview {
  asOf: string;
  periodStart: string;
  ai: UsageTotals;
  connections: Array<{
    provider: string;
    label: string;
    active: number;
    alerts: number;
  }>;
  webhookFailures: WebhookHealth;
}
export interface SystemHealth {
  asOf: string;
  api: { status: "up"; instance: string; node: string; environment: string };
  database: "connected" | "unavailable";
  databaseDetail: { status: "connected" | "unavailable"; latencyMs: number | null; replicaSet: string | null; transactions: boolean };
  uptimeSeconds: number;
  memoryMb: number;
  webhooks: WebhookHealth & {
    byProvider: Array<{ provider: string; completed: number; failed: number; processing: number }>;
    recentFailures: Array<{ provider: string; at: string; companyUid?: string }>;
  };
  integrations: Array<{
    provider: string;
    label: string;
    configured: number;
    required: number;
    keys: Array<{ key: string; present: boolean }>;
  }>;
  aiProviders: Array<{ provider: string; status: "connected" | "configured" | "missing" | "error" | "disabled"; source: string; lastTestedAt?: string; isActive: boolean }>;
  aiFailures: { total: number; rows: Array<{ provider: string; category: string; count: number; lastAt: string }> };
  workers: Array<{
    name: string; instance: string; enabled: boolean; intervalSeconds?: number;
    lastRunAt?: string; lastSuccessAt?: string; lastFailureAt?: string; lastError?: string; lastDurationMs?: number;
    registeredAt?: string;
    state: "healthy" | "waiting" | "stale" | "failing" | "disabled";
  }>;
  reconnect: { total: number; rows: Array<{ provider: string; companyUid: string; companyName: string; account: string; issue: string; reconnect: boolean; at?: string }> };
  monitoring: { sentry: boolean; logLevel: string; recentErrors: Array<{ at: string; method: string; path: string; name: string; requestId?: string }> };
}
export interface ControlFilters {
  days?: number;
  period?: "today" | "month";
  page?: number;
  companyUid?: string;
  provider?: string;
  model?: string;
  feature?: string;
  billingMode?: string;
  status?: string;
}
export type AiPriceUnit =
  | "inputPerMillion"
  | "outputPerMillion"
  | "cachedInputPerMillion"
  | "cacheReadPerMillion"
  | "cacheWritePerMillion"
  | "reasoningPerMillion"
  | "imagePerUnit"
  | "audioInputPerSecond"
  | "audioOutputPerSecond"
  | "charactersPerThousand"
  | "videoPerSecond";
export interface AiPriceInput {
  provider: string;
  model: string;
  requestType: "chat" | "image" | "video" | "transcription" | "speech" | "embedding";
  quality?: string;
  size?: string;
  rates: Partial<Record<AiPriceUnit, number>>;
  markupPercent: number;
  flatChargeUsd: number;
  effectiveFrom: string;
  enabled: boolean;
}
export interface AiPriceRow extends AiPriceInput {
  priceUid: string;
  createdBy: string;
  createdAt: string;
}
const BASE = "/super_admin/control-center";
const get = async <T>(path: string, params?: ControlFilters): Promise<T> =>
  (await api.get(`${BASE}/${path}`, { params })).data;
export const controlCenterService = {
  usageCompanies: async (
    q = "",
  ): Promise<Array<{ companyUid: string; name: string }>> =>
    (await api.get(`${BASE}/usage-companies`, { params: { q } })).data,
  overview: () => get<PlatformOverview>("overview"),
  usage: (params: ControlFilters) => get<UsageReport>("usage", params),
  requests: (params: ControlFilters) =>
    get<PageResult<RequestRow>>("requests", params),
  wallet: (companyUid: string) =>
    get<AiWalletBalance>(`wallets/${encodeURIComponent(companyUid)}`),
  ledger: (companyUid: string, page = 1) =>
    get<PageResult<AiCreditEntry>>(
      `wallets/${encodeURIComponent(companyUid)}/ledger`,
      { page },
    ),
  adjustWallet: async (
    companyUid: string,
    input: {
      kind: "grant" | "deduct" | "allowance";
      credits: number;
      reason: string;
      operationUid: string;
    },
  ): Promise<AiWalletBalance> =>
    (
      await api.post(
        `${BASE}/wallets/${encodeURIComponent(companyUid)}/adjust`,
        input,
      )
    ).data,
  credits: (params: ControlFilters) =>
    get<PageResult<WalletRow>>("credits", params),
  models: () => get<ModelCatalog>("models"),
  prices: () => get<AiPriceRow[]>("prices"),
  createPrice: async (input: AiPriceInput): Promise<AiPriceRow> =>
    (await api.post(`${BASE}/prices`, input)).data,
  whatsapp: (params: ControlFilters & { attention?: "1" }) =>
    get<PageResult<WhatsappHealthRow> & { summary: WhatsappHealthSummary }>("whatsapp", params),
  system: () => get<SystemHealth>("system"),
};
